import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api, ApiError, formatDate } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import type {
  CitationResolution,
  Legislation,
  LegislationKind,
  LegislationStatus,
  Page,
  ProvisionSearchResult,
} from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { EmptyState, ErrorNote, Field, Modal, PageHeader, Spinner, StatusBadge, Tabs } from '../../ui/components';

const KINDS: LegislationKind[] = ['ACT', 'SUBSIDIARY_LEGISLATION'];
const STATUSES: LegislationStatus[] = ['IN_FORCE', 'REPEALED'];

const TABS = [
  { key: 'browse', label: 'Browse legislation' },
  { key: 'sections', label: 'Search within sections' },
  { key: 'citation', label: 'Resolve a citation' },
] as const;
type TabKey = (typeof TABS)[number]['key'];

/**
 * SeyLII-grade public law portal: GET /api/v1/m38/legislation, GET
 * /api/v1/m38/provisions/search, and GET /api/v1/m38/citations/resolve are
 * all genuinely unauthenticated in m38-attorney-general/src/routes/{legislation,
 * provisions,citations}.ts — no bearer token required. This page renders
 * identically whether or not a persona is signed in; LAW_REVISION_COMMISSIONER
 * gets one extra "+ New legislation" action.
 */
export function LawSearch() {
  const { persona } = useAuth();
  const canManage = persona?.roles.includes('LAW_REVISION_COMMISSIONER') ?? false;
  const [tab, setTab] = useState<TabKey>('browse');

  return (
    <div>
      <PageHeader
        title="Law of Seychelles"
        subtitle="Consolidated, versioned legislation with point-in-time views, amendment linkage, and citation resolution — public record, no sign-in required."
      />
      <Tabs tabs={TABS} value={tab} onChange={setTab} />
      {tab === 'browse' ? <BrowsePanel canManage={canManage} /> : null}
      {tab === 'sections' ? <SectionSearchPanel /> : null}
      {tab === 'citation' ? <CitationPanel /> : null}
    </div>
  );
}

function BrowsePanel({ canManage }: { canManage: boolean }) {
  const [q, setQ] = useState('');
  const [status, setStatus] = useState<LegislationStatus | ''>('');
  const [kind, setKind] = useState<LegislationKind | ''>('');
  const [showCreate, setShowCreate] = useState(false);
  const navigate = useNavigate();

  const query = new URLSearchParams({ limit: '50' });
  if (q.trim()) query.set('q', q.trim());
  if (status) query.set('status', status);
  if (kind) query.set('kind', kind);
  const listQ = useApiQuery<Page<Legislation>>(`/api/v1/m38/legislation?${query.toString()}`);

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-end gap-3">
        <Field label="Search title / instrument number / category" className="min-w-[260px] flex-1">
          <input className="field-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="e.g. Civil Status Act, Cap 45…" />
        </Field>
        <Field label="Kind" className="!mb-0">
          <select className="field-input !w-auto" value={kind} onChange={(e) => setKind(e.target.value as LegislationKind | '')}>
            <option value="">All</option>
            {KINDS.map((k) => (
              <option key={k} value={k}>
                {k.replaceAll('_', ' ')}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Status" className="!mb-0">
          <select className="field-input !w-auto" value={status} onChange={(e) => setStatus(e.target.value as LegislationStatus | '')}>
            <option value="">All</option>
            {STATUSES.map((s) => (
              <option key={s} value={s}>
                {s.replaceAll('_', ' ')}
              </option>
            ))}
          </select>
        </Field>
        {canManage ? (
          <button className="btn-primary" onClick={() => setShowCreate(true)}>
            + New legislation
          </button>
        ) : null}
      </div>

      {listQ.error ? <ErrorNote message={listQ.error} /> : null}
      <div className="card overflow-x-auto">
        {listQ.loading ? (
          <Spinner />
        ) : (listQ.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="No legislation matches" hint="Try broadening the search or clearing filters." />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">Short title</th>
                <th className="table-head">Instrument no.</th>
                <th className="table-head">Kind</th>
                <th className="table-head">Category</th>
                <th className="table-head">Status</th>
              </tr>
            </thead>
            <tbody>
              {listQ.data!.items.map((l) => (
                <tr key={l.id} className="cursor-pointer hover:bg-slate-50" onClick={() => navigate(`/law/${l.id}`)}>
                  <td className="table-cell font-medium">
                    <Link to={`/law/${l.id}`} className="text-brand-700 hover:underline" onClick={(e) => e.stopPropagation()}>
                      {l.shortTitle}
                    </Link>
                    {l.chapterNo ? <span className="ml-1.5 text-xs text-slate-400">({l.chapterNo})</span> : null}
                  </td>
                  <td className="table-cell text-xs text-slate-500">{l.instrumentNumber}</td>
                  <td className="table-cell text-xs">{l.kind.replaceAll('_', ' ')}</td>
                  <td className="table-cell text-xs text-slate-500">{l.category ?? '—'}</td>
                  <td className="table-cell">
                    <StatusBadge status={l.status} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showCreate ? (
        <CreateLegislationModal
          onClose={() => setShowCreate(false)}
          onDone={(id) => {
            setShowCreate(false);
            navigate(`/law/${id}`);
          }}
        />
      ) : null}
    </div>
  );
}

function CreateLegislationModal({ onClose, onDone }: { onClose: () => void; onDone: (id: string) => void }) {
  const [kind, setKind] = useState<LegislationKind>('ACT');
  const [title, setTitle] = useState('');
  const [shortTitle, setShortTitle] = useState('');
  const [instrumentNumber, setInstrumentNumber] = useState('');
  const [chapterNo, setChapterNo] = useState('');
  const [category, setCategory] = useState('');
  const [firstEnactedDate, setFirstEnactedDate] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const created = await api.post<Legislation>('/api/v1/m38/legislation', {
        kind,
        title,
        shortTitle,
        instrumentNumber,
        chapterNo: chapterNo || undefined,
        category: category || undefined,
        firstEnactedDate: firstEnactedDate ? new Date(`${firstEnactedDate}T00:00:00.000Z`).toISOString() : undefined,
      });
      onDone(created.id);
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to create legislation shell');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="New legislation shell" onClose={onClose} wide>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <p className="text-xs text-slate-500">
          Creates the registry shell only — publish a first consolidated version afterwards from the legislation's own page.
        </p>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Kind">
            <select className="field-input" value={kind} onChange={(e) => setKind(e.target.value as LegislationKind)}>
              {KINDS.map((k) => (
                <option key={k} value={k}>
                  {k.replaceAll('_', ' ')}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Chapter no. (optional)">
            <input className="field-input" value={chapterNo} onChange={(e) => setChapterNo(e.target.value)} placeholder="e.g. Cap 45" />
          </Field>
        </div>
        <Field label="Full title">
          <input className="field-input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. An Act to consolidate the law relating to…" />
        </Field>
        <Field label="Short (citable) title">
          <input className="field-input" value={shortTitle} onChange={(e) => setShortTitle(e.target.value)} placeholder="e.g. Civil Status Act" />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Instrument number">
            <input className="field-input" value={instrumentNumber} onChange={(e) => setInstrumentNumber(e.target.value)} placeholder="e.g. Act 4 of 2023" />
          </Field>
          <Field label="Category (optional)">
            <input className="field-input" value={category} onChange={(e) => setCategory(e.target.value)} placeholder="e.g. Land, Criminal Law…" />
          </Field>
        </div>
        <Field label="First enacted date (optional)">
          <input type="date" className="field-input" value={firstEnactedDate} onChange={(e) => setFirstEnactedDate(e.target.value)} />
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn-primary"
            onClick={submit}
            disabled={busy || title.trim().length < 3 || shortTitle.trim().length < 3 || instrumentNumber.trim().length < 2}
          >
            {busy ? 'Creating…' : 'Create'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function SectionSearchPanel() {
  const [q, setQ] = useState('');
  const [at, setAt] = useState('');
  const [results, setResults] = useState<ProvisionSearchResult[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  const search = async () => {
    if (q.trim().length < 2) {
      setError('Enter at least 2 characters.');
      return;
    }
    setLoading(true);
    setError(null);
    setSearched(true);
    try {
      const params = new URLSearchParams({ q: q.trim(), limit: '50' });
      if (at) params.set('at', at);
      const page = await api.get<Page<ProvisionSearchResult>>(`/api/v1/m38/provisions/search?${params.toString()}`);
      setResults(page.items);
    } catch (e) {
      setResults(null);
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Search failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <p className="mb-3 text-sm text-slate-500">
        Full-text search across every instrument's sections (current in-force text by default, or a point-in-time snapshot via
        "as at").
      </p>
      <div className="mb-4 flex flex-wrap items-end gap-3">
        <Field label="Search text" className="min-w-[260px] flex-1">
          <input
            className="field-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && search()}
            placeholder='e.g. "registration of title"'
          />
        </Field>
        <Field label="As at (optional)" className="!mb-0">
          <input type="date" className="field-input" value={at} onChange={(e) => setAt(e.target.value)} />
        </Field>
        <button className="btn-primary" onClick={search} disabled={loading}>
          {loading ? 'Searching…' : 'Search'}
        </button>
      </div>

      {error ? <ErrorNote message={error} /> : null}
      {loading ? <Spinner label="Searching sections…" /> : null}

      {!loading && searched && results && results.length === 0 ? (
        <EmptyState title="No sections matched" hint="Try a shorter or different phrase." />
      ) : null}

      {!loading && results && results.length > 0 ? (
        <ul className="space-y-2">
          {results.map((r) => (
            <li key={r.id}>
              <Link to={`/law/${r.legislation.id}`} className="block rounded-lg border border-slate-200 bg-white px-4 py-3 hover:border-brand-400">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-semibold text-slate-900">
                    {r.legislation.shortTitle} — s.{r.sectionNumber}
                    {r.heading ? <span className="font-normal text-slate-500"> ({r.heading})</span> : null}
                  </span>
                  <span className="text-xs text-slate-400">v{r.version.versionNumber}</span>
                </div>
                <p className="mt-1 line-clamp-2 text-xs text-slate-600">{r.bodyText}</p>
              </Link>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

function CitationPanel() {
  const [ref, setRef] = useState('');
  const [at, setAt] = useState('');
  const [result, setResult] = useState<CitationResolution | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const resolve = async () => {
    if (!ref.trim()) return;
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ ref: ref.trim() });
      if (at) params.set('at', at);
      const res = await api.get<CitationResolution>(`/api/v1/m38/citations/resolve?${params.toString()}`);
      setResult(res);
    } catch (e) {
      setResult(null);
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Could not resolve citation');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <p className="mb-3 text-sm text-slate-500">
        Resolves a free-text legal reference to a specific citable provision — the same lookup every module's <code>legalRef</code>{' '}
        field uses under the hood (<code>law:get-provision</code>).
      </p>
      <div className="mb-4 flex flex-wrap items-end gap-3">
        <Field label="Citation" className="min-w-[260px] flex-1">
          <input
            className="field-input"
            value={ref}
            onChange={(e) => setRef(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && resolve()}
            placeholder="e.g. Land Registration Act s.42"
          />
        </Field>
        <Field label="As at (optional)" className="!mb-0">
          <input type="date" className="field-input" value={at} onChange={(e) => setAt(e.target.value)} />
        </Field>
        <button className="btn-primary" onClick={resolve} disabled={loading || !ref.trim()}>
          {loading ? 'Resolving…' : 'Resolve'}
        </button>
      </div>

      {error ? <ErrorNote message={error} /> : null}
      {loading ? <Spinner label="Resolving citation…" /> : null}

      {!loading && result ? (
        <div className="card p-5">
          {result.matched ? (
            <>
              <div className="mb-2 flex items-center gap-2">
                <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700 ring-1 ring-emerald-200">
                  Matched
                </span>
                {result.at ? <span className="text-xs text-slate-400">as at {formatDate(result.at)}</span> : null}
              </div>
              {result.legislation ? (
                <Link to={`/law/${result.legislation.id}`} className="text-sm font-semibold text-brand-700 hover:underline">
                  {result.legislation.shortTitle} ({result.legislation.instrumentNumber})
                </Link>
              ) : null}
              {result.version ? (
                <div className="mt-1 text-xs text-slate-500">
                  Version {result.version.versionNumber}, effective {formatDate(result.version.effectiveFrom)}
                  {result.version.effectiveTo ? ` – ${formatDate(result.version.effectiveTo)}` : ' – present'}
                </div>
              ) : null}
              {result.provision ? (
                <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 p-3.5">
                  <div className="text-xs font-bold uppercase tracking-wide text-slate-500">
                    Section {result.provision.sectionNumber}
                    {result.provision.heading ? ` — ${result.provision.heading}` : ''}
                  </div>
                  <p className="mt-1 whitespace-pre-wrap text-sm text-slate-700">{result.provision.bodyText}</p>
                </div>
              ) : result.sectionRequested ? (
                <div className="mt-3">
                  <ErrorNote message={`Section "${result.sectionRequested}" was not found in this version.`} />
                </div>
              ) : null}
            </>
          ) : result.ambiguous ? (
            <>
              <div className="mb-2 rounded-full inline-block bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-700 ring-1 ring-amber-200">
                Ambiguous — {result.candidates?.length ?? 0} candidates
              </div>
              <ul className="mt-2 space-y-1.5">
                {result.candidates?.map((c) => (
                  <li key={c.id}>
                    <Link to={`/law/${c.id}`} className="text-sm text-brand-700 hover:underline">
                      {c.shortTitle} ({c.instrumentNumber})
                    </Link>
                  </li>
                ))}
              </ul>
            </>
          ) : (
            <EmptyState title="No match" hint={`Nothing in the legislation registry matches "${result.query}".`} />
          )}
        </div>
      ) : null}
    </div>
  );
}
